import {
  getOrganizationUsers,
  patchOrganization,
} from "@/api/endpoints/organization";
import { FEATURE } from "@/features";
import { useCanManageUsers } from "@/hooks/useCanManageUsers";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useOrganization } from "@/hooks/useOrganization";
import { ROUTES } from "@/routes_new";
import { Button } from "@components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import ErrorComponent from "@components/ui/error";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldTitle,
} from "@components/ui/field";
import { Skeleton } from "@components/ui/skeleton";
import { Switch } from "@components/ui/switch";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { generatePath, Link, Route, useParams } from "react-router-dom";
import Heading from "../Heading";
import UserSheet from "./partials/UserSheet";
import UsersTable from "./partials/UsersTable";
import DeleteUser from "./partials/DeleteUser";
import InviteUser from "./partials/InviteUser";
import AllEarsTitle from "@components/AllEarsTitle";

export default function SettingsUsersView() {
  const { t } = useLingui();

  const title = t`Användare`;

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const canManageUsers = useCanManageUsers();

  const {
    data: usersData,
    error: usersError,
    isLoading: isUsersLoading,
  } = useQuery({
    queryKey: [orgUuid, "users"],
    queryFn: () => getOrganizationUsers(orgUuid),
    enabled: !!orgUuid,
  });

  const { data: orgData, refetch: refetchOrg } = useOrganization();

  // SSO enforcement: plan must grant access, admin can toggle
  const canEnforceSso = useHasFeature(FEATURE.HAS_FORCE_SSO_LOGIN_FEATURE);

  const { mutate: toggleSso, isPending: isSsoPending } = useMutation({
    mutationFn: (allowSsoLoginOnly: boolean) => {
      if (!orgData?.ok) throw new Error();
      return patchOrganization(orgData.data.uuid, {
        allow_sso_login_only: allowSsoLoginOnly,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) throw new Error();
      refetchOrg();
      toast.success(
        response.data.allow_sso_login_only
          ? t`SSO-krav för inloggning är nu aktiverat`
          : t`SSO-krav för inloggning är nu inaktivt`
      );
    },
    onError: () => {
      toast.error(t`Kunde inte uppdatera inställningen`);
    },
  });

  const { isVisible: showSsoSpinner } = useDelayedLoading(isSsoPending);

  return (
    <>
      <AllEarsTitle title={title} />

      <Route
        path={ROUTES.SETTINGS_TEAM_INVITE_USER_ORGID}
        component={InviteUser}
      />
      <Route
        exact
        path={ROUTES.SETTINGS_TEAM_USER_ORGID}
        component={UserSheet}
      />
      <Route
        path={ROUTES.SETTINGS_TEAM_DELETE_USER_ORGID}
        component={DeleteUser}
      />

      <Heading
        heading={title}
        description={t`Bjud in kollegor till All Ears och hantera deras åtkomst`}
      />

      <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              <Trans>Användare</Trans>
            </CardTitle>
            <CardAction>
              <Button asChild>
                <Link
                  to={generatePath(ROUTES.SETTINGS_TEAM_INVITE_USER_ORGID, {
                    orgUuid,
                  })}
                >
                  <Trans>Bjud in kollega</Trans>
                  <PlusIcon />
                </Link>
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {usersData ? (
              usersData.ok && Array.isArray(usersData.data) ? (
                <UsersTable
                  users={usersData.data}
                  orgUuid={orgUuid}
                  canManageUsers={canManageUsers}
                />
              ) : (
                <ErrorComponent
                  heading={t`Åh nej!`}
                  description={t`Något gick fel när vi försökte ladda in användare.`}
                />
              )
            ) : isUsersLoading ? (
              <Skeleton className="h-32" />
            ) : usersError ? (
              <ErrorComponent
                heading={t`Åh nej!`}
                description={t`Något gick fel när vi försökte ladda in användare.`}
                error={usersError}
              />
            ) : null}
          </CardContent>
        </Card>

        {canManageUsers && canEnforceSso && orgData?.ok && (
          <Card className="p-0 gap-0">
            <CardHeader className="p-6 pb-0">
              <CardTitle className="text-xl">
                <Trans>Säkerhet</Trans>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 py-5">
              <Field orientation="horizontal">
                <FieldContent>
                  <FieldTitle>
                    <Trans>Kräv SSO-inloggning</Trans>
                  </FieldTitle>
                  <FieldDescription>
                    <Trans>
                      Användare kan enbart logga in via SSO (Google eller
                      Microsoft). Inloggning med lösenord blockeras.
                    </Trans>
                  </FieldDescription>
                </FieldContent>
                <Switch
                  checked={orgData.data.allow_sso_login_only}
                  onCheckedChange={(checked) => toggleSso(checked)}
                  disabled={isSsoPending}
                  isLoading={showSsoSpinner}
                  aria-label={t`Kräv SSO-inloggning`}
                  data-testid="sso-toggle"
                />
              </Field>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
